import {
  Button,
  Checkbox,
  Classes,
  Dialog,
  Intent,
  Label,
  NumericInput,
  Radio,
  RadioGroup,
  Tabs,
  Tab,
  TagInput,
  MenuItem,
  Tooltip,
  Position,
} from '@blueprintjs/core';
import { IItemRendererProps } from '@blueprintjs/select';
import { IconNames } from '@blueprintjs/icons';
import { TranslatedMessage } from './translation';
import { TLCode } from '../../common/enums';
import { Form } from 'react-final-form';
import { TextInputFormField, GroupFormField, PharmacyFormField2, GroupsFormField } from '../common/form';
import Select from 'react-select';
import { WithTranslateProps } from './WithTranslate';
import DateInput from '../common/input/DateInput';
import { TextInput } from '../common/input/basicInputs';
import { Instance } from 'mobx-state-tree';
import { Pharmacy } from '../../store/models';
import { Group } from '../../store/models/Group';
import { MessageSelectionMode, MessageToSend } from '../../store/models/Message';
import { useCallback, useEffect, useState, useMemo } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import Dropzone from 'react-dropzone';
import { css } from 'emotion';
import HtmlEditor, { MediaResizing } from 'devextreme-react/html-editor';
import { DivLineWithButtonMargin } from './styledComponents/StyledComponents';
import { MessagePreviewMailConfirmation } from './dialog/MessagePreviewMailConfirmation';

interface MessageDialogProps extends WithTranslateProps {
  isOpen: boolean;
  isPreloaded: boolean;
  onClose: () => void;
  pharmacyId: number | undefined;
  secretariatId: number | undefined;
  save: (messageToSave: any) => void;
  pharmacyList: Array<Instance<typeof Pharmacy>>;
  groupList: Array<Instance<typeof Group>>;
  isSecretariat: boolean;
  isSuperAdmin?: boolean;
  isTest?: boolean;
  canPreview?: boolean;
  onPreviewClick: (messageToSave: any) => boolean;
  preloadedRecipientIds?: number[];
  canOutsideClickClose?: boolean;
}

export const MessageDialog = ({
  isOpen,
  isPreloaded,
  onClose,
  save,
  secretariatId,
  pharmacyList,
  groupList,
  translate,
  isSecretariat,
  isSuperAdmin = false,
  isTest = false,
  canPreview = false,
  onPreviewClick,
  preloadedRecipientIds,
  canOutsideClickClose = false
}: MessageDialogProps) => {
  const defaultMessage = {
    messageId: 0,
    body: '',
    htmlContent: '',
    title: isTest ? 'Lorem ipsum dolor' : '',
    recipientId: isSecretariat ? undefined : secretariatId,
    recipientIds: preloadedRecipientIds != undefined ? preloadedRecipientIds : [],
    selectionMode: isSuperAdmin
      ? MessageSelectionMode.FromTableResult
      : isTest
        ? MessageSelectionMode.Test
        : (isSecretariat
          ? secretariatId
          : undefined)
          ? isPreloaded
            ? MessageSelectionMode.Several
            : MessageSelectionMode.One
          : '',
    dutyType: '',
    dutyDate: null,
    dayOnly: true,
    nightOnly: true,
    dayNight: true,
    radius: 0,
    groupId: undefined,
    groupIds: [],
    secretariatId: secretariatId,
    fileList: [],
    fileData: [],
    fileNames: [],
    fileTypes: [],
    testEmailAddress: null
  } as MessageToSend;

  const [messageToSend, setMessageToSend] = useState(defaultMessage);
  const [previewConfirmationInProgress, setPreviewConfirmationInProgress] = useState(false);
  const [previewConfirmationInError, setPreviewConfirmationInError] = useState(false);
  const [previewConfirmationDone, setPreviewConfirmationDone] = useState(false);
  //const [testEmailAddress, setTestEmailAddress] = useState('');

  useEffect(() => {
    if (preloadedRecipientIds) {
      setMessageToSend({
        ...messageToSend,
        recipientIds: preloadedRecipientIds,
      });
    } 
  }, [preloadedRecipientIds]);

  const setFileNames = useCallback(
    (value: any) => {
      messageToSend.fileData.push(value);
      setMessageToSend({ ...messageToSend });
    },
    [setMessageToSend, messageToSend],
  );

  const removeFileNames = useCallback(
    (value: any, index: number) => {
      messageToSend.fileData.splice(index, 1);
      messageToSend.fileNames.splice(index, 1);
      messageToSend.fileTypes.splice(index, 1);
      messageToSend.fileList.splice(index, 1);
      setMessageToSend({ ...messageToSend });
    },
    [setMessageToSend, messageToSend],
  );

  const setGroupId = useCallback(
    (value: number | undefined) => {
      setMessageToSend({ ...messageToSend, groupId: value });
    },
    [setMessageToSend, messageToSend],
  );

  const setRecipientIds = useCallback(
    (value: any) => {
      setMessageToSend({
        ...messageToSend,
        recipientIds: value.map((v) => v.value),
      });
    },
    [setMessageToSend, messageToSend],
  );

  const setRecipientId = useCallback(
    (value: number | undefined) => {
      setMessageToSend({ ...messageToSend, recipientId: value });
    },
    [setMessageToSend, messageToSend],
  );

  const setSelectionMode = useCallback(
    (data) => {
      const isNeighborRadio = data === MessageSelectionMode.Neighbors;
      const isOnePharmacyRadio = data === MessageSelectionMode.One;
      setMessageToSend({
        ...messageToSend,
        selectionMode: data,
        recipientId: isOnePharmacyRadio || isNeighborRadio ? messageToSend.recipientId : undefined,
        groupId: undefined,
        recipientIds: [],
        radius: 0,
        dutyDate: null,
        dayOnly: true,
        nightOnly: true,
        dayNight: true,
      });
    },
    [setMessageToSend, messageToSend],
  );

  const setTestEmailAddress = useCallback(
    (data) => {
      setMessageToSend({
        ...messageToSend,
        testEmailAddress: data
      });
    },
    [setMessageToSend, messageToSend]
  );

  const onDutyDateChange = useCallback(
    (data: Date) => {
      setMessageToSend({ ...messageToSend, dutyDate: data });
    },
    [setMessageToSend, messageToSend],
  );

  const onNightOnlyChange = useCallback(
    (data) => {
      setMessageToSend({ ...messageToSend, nightOnly: data.currentTarget.checked });
    },
    [setMessageToSend, messageToSend],
  );

  const onDayNightChange = useCallback(
    (data) => {
      setMessageToSend({ ...messageToSend, dayNight: data.currentTarget.checked });
    },
    [setMessageToSend, messageToSend],
  );

  const onDayOnlyChange = useCallback(
    (data) => {
      setMessageToSend({ ...messageToSend, dayOnly: data.currentTarget.checked });
    },
    [setMessageToSend, messageToSend],
  );

  const onRadiusChange = useCallback(
    (dataAsNumber, dataAsString) => {
      setMessageToSend({ ...messageToSend, radius: dataAsNumber });
    },
    [setMessageToSend, messageToSend],
  );

  const onTitleChange = useCallback(
    (data) => {
      setMessageToSend({ ...messageToSend, title: data.target.value });
    },
    [setMessageToSend, messageToSend],
  );

  const resetPreviewMailDialogState = useCallback(() => {
    setPreviewConfirmationDone(false);
    setPreviewConfirmationInError(false);
    setPreviewConfirmationInProgress(false);
  }, []);

  const getPharmacyDisplayForMultiSelect = useCallback(
    (elem) => {
      return {
        value: elem.pharmacyId,
        label:
          '(' +
          elem.level +
          '- group ' +
          (elem.groupId === undefined ? '' : groupList.find((g) => g.groupId === elem.groupId)!.name) +
          ') - ' +
          elem.apbNumber +
          ' ' +
          elem.name +
          ' @ ' +
          elem.city,
      };
    },
    [groupList],
  );

  const sizeValues = ['8pt', '10pt', '12pt', '14pt', '18pt', '24pt', '36pt'];
  const headerValues = [false, 1, 2, 3, 4, 5];

  const fontSizeOptions = {
    inputAttr: {
      'aria-label': 'Font size',
    },
  };
  const fontFamilyOptions = {
    inputAttr: {
      'aria-label': 'Font family',
    },
  };
  const headerOptions = {
    inputAttr: {
      'aria-label': 'Font family',
    },
  };

  const handleHtmlChange = useCallback(
    (e) => {
      const newhtmlContent = e;
      if (newhtmlContent != messageToSend.htmlContent) {
        setMessageToSend({ ...messageToSend, htmlContent: e, body: e });
      }
    },
    [setMessageToSend, messageToSend],
  );

  const renderGroupMenuItem = (item: Instance<typeof Group>, { handleClick, index, modifiers }: IItemRendererProps) => {
    if (modifiers.matchesPredicate) {
      return (
        <MenuItem
          key={`group-${index}`}
          active={modifiers.active}
          onClick={handleClick}
          text={item.name + ' - ' + item.description!}
        />
      );
    } else {
      return null;
    }
  };

  const handleSelect = function (group) {
    var currentGroupIds = messageToSend.groupIds != null ? messageToSend.groupIds : [];

    if (currentGroupIds.indexOf(group.groupId) === -1) {
      var updatedGroupIds = currentGroupIds.concat(group.groupId);
      setMessageToSend({ ...messageToSend, groupIds: updatedGroupIds });
    }
  };

  const handleRemove = function (_tag, index) {
    var currentGroupIds = messageToSend.groupIds != null ? messageToSend.groupIds : [];

    var selectedGroups = groupList.filter(function (group) {
      return currentGroupIds.indexOf(group.groupId) !== -1;
    });

    var updatedGroups = selectedGroups.filter(function (_, i) {
      return i !== index;
    });

    var updatedGroupIds = updatedGroups.map(function (g) {
      return g.groupId;
    });

    setMessageToSend({ ...messageToSend, groupIds: updatedGroupIds });
  };

  const selectedGroups = groupList.filter(function (group) {
    return messageToSend.groupIds && messageToSend.groupIds.indexOf(group.groupId) !== -1;
  });

  const clearSelectedItems = () => {
    setMessageToSend({ ...messageToSend, groupIds: [] });
  };

  const toolbar = useMemo(() => {
    return {
      items: [
        { name: 'undo' },
        { name: 'redo' },
        { name: 'separator' },
        { name: 'size', acceptedValues: sizeValues, options: { fontSizeOptions } },
        {
          name: 'font',
          acceptedValues: [
            'Arial',
            'Courier New',
            'Georgia',
            'Impact',
            'Lucida Console',
            'Tahoma',
            'Times New Roman',
            'Verdana',
          ],
          options: { fontFamilyOptions },
        },
        { name: 'separator' },
        { name: 'bold' },
        { name: 'italic' },
        { name: 'strike' },
        { name: 'underline' },
        { name: 'separator' },
        { name: 'alignLeft' },
        { name: 'alignCenter' },
        { name: 'alignRight' },
        { name: 'alignJustify' },
        { name: 'increaseindent' },
        { name: 'decreaseIndent' },
        { name: 'separator' },
        { name: 'orderedList' },
        { name: 'bulletList' },
        { name: 'separator' },
        { name: 'header', acceptedValues: headerValues, options: { headerOptions } },
        { name: 'separator' },
        { name: 'color' },
        { name: 'background' },
        { name: 'separator' },
        { name: 'link' },
        { name: 'image' },
        { name: 'separator' },
        { name: 'clear' },
        { name: 'codeBlock' },
        { name: 'blockquote' },
        { name: 'separator' },
        { name: 'insertTable' },
        { name: 'deleteTable' },
        { name: 'insertRowAbove' },
        { name: 'insertRowBelow' },
        { name: 'deleteRow' },
        { name: 'insertColumnLeft' },
        { name: 'insertColumnRight' },
        { name: 'deleteColumn' },
        { name: 'separator' },
        { name: 'tableProperties' },
      ],
    };
  }, []);

  const currentGroupIds = messageToSend.groupIds != null ? messageToSend.groupIds : [];

  const availableGroups = groupList.filter(function (group) {
    return currentGroupIds.indexOf(group.groupId) === -1;
  });

  return (
    <>
      <MessagePreviewMailConfirmation
        translate={translate}
        inProgress={previewConfirmationInProgress}
        inError={previewConfirmationInError}
        done={previewConfirmationDone}
      />
      <Dialog
        isOpen={isOpen}
        onClose={() => {
          setMessageToSend(defaultMessage);
          onClose();
        }}
        icon={IconNames.EDIT}
        title={<TranslatedMessage id={TLCode.MESSAGE_NewMessage} />}
        style={{ width: '900px' }}
        canOutsideClickClose={canOutsideClickClose}
      >
        <div className={Classes.DIALOG_BODY}>
          <Form
            initialValues={messageToSend}
            onSubmit={(values) => {
              values['fileData'] = messageToSend.fileData;
              //values["fileList"] = messageToSend.fileList;
              save(values);

              setMessageToSend(defaultMessage);
            }}
            render={({ handleSubmit, hasValidationErrors, values }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <Tabs
                    defaultSelectedTabId={isPreloaded ? 'tab2' : isTest ? 'tab1B' : 'tab1A'}
                    renderActiveTabPanelOnly={false}
                  >
                    {!isTest && !isPreloaded && (
                      <Tab
                        title="Destinataires"
                        id="tab1A"
                        panel={
                          <>
                            <RadioGroup
                              selectedValue={messageToSend.selectionMode}
                              onChange={(data) => {
                                setSelectionMode(data.currentTarget.value);
                              }}
                              inline={true}
                            >
                              <Radio
                                key={MessageSelectionMode.Test}
                                label={translate(TLCode.MESSAGE_RECIPIENTS_Test)}
                                value={MessageSelectionMode.Test}
                                style={{ width: 'fit-content' }}
                              />
                              {messageToSend.selectionMode === MessageSelectionMode.Test && (
                                <>
                                  <Label>
                                    <b>
                                      <TranslatedMessage id={TLCode.MESSAGE_TestEmailAddress} />
                                    </b>
                                    &nbsp;&nbsp;
                                  </Label>
                                  <TextInput
                                    value={messageToSend.testEmailAddress== null?'':messageToSend.testEmailAddress}
                                    required={true}
                                    fill={true}
                                    onChange={(data) => {
                                      setTestEmailAddress(data.currentTarget.value);
                                    }}
                                  />
                                </>
                              )}
                            </RadioGroup>
                            <RadioGroup
                              selectedValue={messageToSend.selectionMode}
                              onChange={(data) => {
                                setSelectionMode(data.currentTarget.value);
                              }}
                              inline={true}
                            >
                              <Radio
                                key={MessageSelectionMode.Affiliates1}
                                label={translate(TLCode.MESSAGE_RECIPIENTS_Affiliates_1)}
                                value={MessageSelectionMode.Affiliates1}
                              />
                              <Radio
                                key={MessageSelectionMode.Affiliates3}
                                label={translate(TLCode.MESSAGE_RECIPIENTS_Affiliates_3)}
                                value={MessageSelectionMode.Affiliates3}
                              />
                              <Radio
                                key={MessageSelectionMode.AffiliatesAll}
                                label={translate(TLCode.MESSAGE_RECIPIENTS_Affiliates_All)}
                                value={MessageSelectionMode.AffiliatesAll}
                              />
                            </RadioGroup>
                            <RadioGroup
                              selectedValue={messageToSend.selectionMode}
                              onChange={(data) => setSelectionMode(data.currentTarget.value)}
                            >
                              {' '}
                              <Radio
                                key={MessageSelectionMode.AffiliatesGroupOne}
                                label={translate(TLCode.MESSAGE_RECIPIENTS_Group_one)}
                                value={MessageSelectionMode.AffiliatesGroupOne}
                                style={{ width: 'fit-content' }}
                              />
                              {messageToSend.selectionMode === MessageSelectionMode.AffiliatesGroupOne && (
                                <GroupFormField
                                  id="groupSelect"
                                  label={TLCode.PHARMACIES_Group}
                                  name="groupId"
                                  groupList={groupList.filter(
                                    (g) => secretariatId === undefined || g.secretariatId === secretariatId,
                                  )}
                                  isClearable={true}
                                  disabled={messageToSend.selectionMode !== MessageSelectionMode.AffiliatesGroupOne}
                                  onGroupIdChanged={setGroupId}
                                  groupId={
                                    messageToSend.selectionMode === MessageSelectionMode.AffiliatesGroupOne
                                      ? messageToSend.groupId
                                      : undefined
                                  }
                                />
                              )}
                              <Radio
                                key={MessageSelectionMode.AffiliatesGroupSeveral}
                                label={translate(TLCode.MESSAGE_RECIPIENTS_Group_several)}
                                value={MessageSelectionMode.AffiliatesGroupSeveral}
                                style={{ width: 'fit-content' }}
                              />
                              {messageToSend.selectionMode === MessageSelectionMode.AffiliatesGroupSeveral && (
                                <GroupsFormField
                                  items={availableGroups}
                                  itemRenderer={renderGroupMenuItem}
                                  tagRenderer={(item: Instance<typeof Group>) => item.name}
                                  selectedItems={selectedGroups}
                                  clearSelectedItems={clearSelectedItems}
                                  onItemSelect={(item) => handleSelect(item)}
                                  itemPredicate={(query, item) => {
                                    return item.name.toLocaleLowerCase().includes(query.toLocaleLowerCase());
                                  }}
                                  tagInputProps={{
                                    onRemove: handleRemove,
                                  }}
                                  placeholder="Select"
                                />
                              )}
                              <Radio
                                key={MessageSelectionMode.One}
                                label={translate(TLCode.MESSAGE_RECIPIENTS_One)}
                                value={MessageSelectionMode.One}
                                style={{ width: 'fit-content' }}
                              />
                              {messageToSend.selectionMode === MessageSelectionMode.One && (
                                <PharmacyFormField2
                                  id="pharmacySelect"
                                  name="recipientId"
                                  pharmacyList={pharmacyList
                                    .filter((f) => secretariatId === undefined || f.secretariatId === secretariatId)
                                    .sort((a, b) => a.apbNumber - b.apbNumber)}
                                  required={messageToSend.selectionMode === MessageSelectionMode.One}
                                  disabled={messageToSend.selectionMode !== MessageSelectionMode.One}
                                  pharmacyId={
                                    messageToSend.selectionMode === MessageSelectionMode.One
                                      ? messageToSend.recipientId
                                      : undefined
                                  }
                                  onPharmacyIdChanged={setRecipientId}
                                />
                              )}
                              <Radio
                                key={MessageSelectionMode.Several}
                                label={translate(TLCode.MESSAGE_RECIPIENTS_Several)}
                                value={MessageSelectionMode.Several}
                                style={{ width: 'fit-content' }}
                              />
                              {messageToSend.selectionMode === MessageSelectionMode.Several && (
                                <Select
                                  options={pharmacyList
                                    .filter((f) => secretariatId === undefined || f.secretariatId === secretariatId)
                                    .map(getPharmacyDisplayForMultiSelect)}
                                  required={messageToSend.selectionMode === MessageSelectionMode.Several}
                                  disabled={messageToSend.selectionMode !== MessageSelectionMode.Several}
                                  isMulti
                                  value={messageToSend.recipientIds.map((id) => {
                                    return getPharmacyDisplayForMultiSelect(
                                      pharmacyList.find((p) => p.pharmacyId === id)!,
                                    );
                                  })}
                                  onChange={setRecipientIds}
                                />
                              )}
                              <br />
                              <Radio
                                key={MessageSelectionMode.OnDuty}
                                label={translate(TLCode.MESSAGE_RECIPIENTS_PharmaciesOnDuty)}
                                value={MessageSelectionMode.OnDuty}
                                style={{ width: 'fit-content' }}
                              />
                              {messageToSend.selectionMode === MessageSelectionMode.OnDuty && (
                                <DivLineWithButtonMargin>
                                  <DivColumn>
                                    {messageToSend.selectionMode == MessageSelectionMode.OnDuty && (
                                      <Label>
                                        <b>
                                          <TranslatedMessage id={TLCode.MESSAGE_RECIPIENTS_DutyDate} />
                                        </b>
                                        &nbsp;&nbsp;
                                      </Label>
                                    )}
                                    {messageToSend.selectionMode !== MessageSelectionMode.OnDuty && (
                                      <Label style={{ color: '#aaa' }}>
                                        <b>
                                          <TranslatedMessage id={TLCode.MESSAGE_RECIPIENTS_DutyDate} />
                                        </b>
                                        &nbsp;&nbsp;
                                      </Label>
                                    )}
                                    <DateInput
                                      value={messageToSend.dutyDate}
                                      canClearSelection={false}
                                      onChange={(value) => {
                                        const utcDate = new Date(
                                          Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()),
                                        );
                                        onDutyDateChange(utcDate);
                                      }}
                                      disabled={messageToSend.selectionMode !== MessageSelectionMode.OnDuty}
                                    />
                                  </DivColumn>
                                  <DivColumn>
                                    <Checkbox
                                      name="dayOnly"
                                      value={'dayOnly'}
                                      checked={messageToSend.dayOnly}
                                      onChange={onDayOnlyChange}
                                      disabled={messageToSend.selectionMode !== MessageSelectionMode.OnDuty}
                                    >
                                      <TranslatedMessage id={TLCode.MESSAGE_RECIPIENTS_DayOnlyDuty} />
                                    </Checkbox>
                                    <Checkbox
                                      name="nightOnly"
                                      value={'nightOnly'}
                                      checked={messageToSend.nightOnly}
                                      onChange={onNightOnlyChange}
                                      disabled={messageToSend.selectionMode !== MessageSelectionMode.OnDuty}
                                    >
                                      <TranslatedMessage id={TLCode.MESSAGE_RECIPIENTS_NightOnlyDuty} />
                                    </Checkbox>
                                    <Checkbox
                                      name="dayNight"
                                      value={'dayNight'}
                                      checked={messageToSend.dayNight}
                                      onChange={onDayNightChange}
                                      disabled={messageToSend.selectionMode !== MessageSelectionMode.OnDuty}
                                    >
                                      <TranslatedMessage id={TLCode.MESSAGE_RECIPIENTS_DayNightDuty} />
                                    </Checkbox>
                                  </DivColumn>
                                </DivLineWithButtonMargin>
                              )}
                              <Radio
                                key={MessageSelectionMode.Neighbors}
                                label={translate(TLCode.MESSAGE_RECIPIENTS_Neighbors)}
                                value={MessageSelectionMode.Neighbors}
                                style={{ width: 'fit-content' }}
                              />
                              {messageToSend.selectionMode === MessageSelectionMode.Neighbors && (
                                <DivLineWithButtonMargin>
                                  <DivColumn>
                                    <PharmacyFormField2
                                      id="pharmacySelectNeighbors"
                                      name="recipientId"
                                      pharmacyList={pharmacyList}
                                      required={messageToSend.selectionMode === MessageSelectionMode.Neighbors}
                                      disabled={messageToSend.selectionMode !== MessageSelectionMode.Neighbors}
                                      pharmacyId={
                                        messageToSend.selectionMode === MessageSelectionMode.Neighbors
                                          ? messageToSend.recipientId
                                          : undefined
                                      }
                                      onPharmacyIdChanged={setRecipientId}
                                    />
                                  </DivColumn>
                                  <DivColumn>
                                    <TranslatedMessage id={TLCode.MESSAGE_RECIPIENTS_Neighbors_radius} />
                                    <br />
                                    <NumericInput
                                      id="radius"
                                      name="radius"
                                      required={messageToSend.selectionMode === MessageSelectionMode.Neighbors}
                                      disabled={messageToSend.selectionMode !== MessageSelectionMode.Neighbors}
                                      onValueChange={onRadiusChange}
                                      value={messageToSend.radius}
                                      majorStepSize={1000}
                                      stepSize={100}
                                    />
                                  </DivColumn>
                                </DivLineWithButtonMargin>
                              )}
                            </RadioGroup>
                          </>
                        }
                      />
                    )}
                    {/* {isTest && !isPreloaded && (
                      <Tab
                        title="Destinataires de test"
                        id="tab1B"
                        panel={
                          <>
                            <TextInputFormField
                              name={testEmailAddress} //"testEmailAddress"
                              label={TLCode.MESSAGE_TestEmailAddress}
                              inline={true}
                              required={true}
                              fill={true}
                              onChange={(data) => {
                                setTestEmailAddress(data.currentTarget.value);
                              }}
                            />
                          </>
                        }
                      />
                    )} */}
                    <Tab
                      title="Contenu"
                      id="tab2"
                      panel={
                        <>
                          <DivLineWithButtonMargin>
                            <Label>
                              <b>
                                <TranslatedMessage id={TLCode.MESSAGE_Title} />
                              </b>
                              &nbsp;&nbsp;
                            </Label>
                            <TextInput
                              value={messageToSend.title ? messageToSend.title : ''}
                              onChange={onTitleChange}
                            />
                          </DivLineWithButtonMargin>
                          <div>{/* <RTEFormField name="body" label={TLCode.MESSAGE_Content} /> */}</div>
                          <div>
                            <b>
                              <TranslatedMessage id={TLCode.MESSAGE_Body} />
                            </b>
                            <HtmlEditor
                              height="200px"
                              value={messageToSend.htmlContent}
                              onValueChange={handleHtmlChange} //ref={htmlEditorRef}
                              toolbar={toolbar}
                              className={bckCol}
                            >
                              <MediaResizing enabled={true} />
                            </HtmlEditor>
                            <br />
                          </div>
                          <div>
                            <b>
                              <TranslatedMessage id={TLCode.MESSAGE_Attachments} />
                            </b>
                            <Dropzone
                              multiple={true}
                              onDrop={(acceptedFiles) => {
                                const reader = new FileReader();
                                reader.onabort = () => console.log('file reading was aborted');
                                reader.onerror = () => console.log('file reading has failed');
                                reader.onload = () => {
                                  // Do whatever you want with the file contents
                                  const fileContent = reader.result;
                                  const byteArray = new Uint8Array(fileContent as ArrayBuffer);
                                  const s: number[] = [];
                                  byteArray.forEach((b) => s.push(b));
                                  setFileNames(s);
                                };
                                acceptedFiles.forEach(async (file) => {
                                  messageToSend.fileList.push(file);
                                  messageToSend.fileNames.push(file.name);
                                  messageToSend.fileTypes.push(file.type);
                                  await reader.readAsArrayBuffer(file);
                                });
                              }}
                            >
                              {({ getRootProps, getInputProps }) => (
                                <section>
                                  <div {...getRootProps()} className={boxed.toString()}>
                                    <input {...getInputProps()} />
                                    <p>{translate(TLCode.MESSAGE_DragAndDrop)}</p>
                                  </div>
                                </section>
                              )}
                            </Dropzone>
                          </div>
                          <br />
                          <b>
                            <TranslatedMessage id={TLCode.MESSAGE_AttachmentList} />
                          </b>
                          <TagInput
                            values={messageToSend.fileNames.map((f) => {
                              return <span>{f}&nbsp;</span>;
                            })}
                            onRemove={removeFileNames}
                            className={bckColGray}
                          />
                          <br />
                          <div className={Classes.DIALOG_FOOTER}>
                            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                              <Button text={<TranslatedMessage id={TLCode.GENERAL_Cancel} />} onClick={onClose} />
                              {canPreview && (
                                <Button
                                  intent={Intent.PRIMARY}
                                  disabled={hasValidationErrors}
                                  text={<TranslatedMessage id={TLCode.MESSAGE_Preview} />}
                                  onClick={async () => {
                                    resetPreviewMailDialogState();
                                    setPreviewConfirmationInProgress(true);
                                    var result = await onPreviewClick(values);
                                    if (result) setPreviewConfirmationDone(true);
                                    else setPreviewConfirmationInError(true);
                                  }}
                                />
                              )}

                              <Tooltip
                                content={translate(TLCode.MESSAGE_Send_to_table_result)}
                                position={Position.BOTTOM}
                                hoverOpenDelay={750}
                                disabled={values.selectionMode !== MessageSelectionMode.FromTableResult}
                              >
                                <Button
                                  type="submit"
                                  intent={Intent.PRIMARY}
                                  disabled={
                                    hasValidationErrors ||
                                    ((values.recipientIds === undefined || values.recipientIds.length === 0) &&
                                      values.selectionMode === MessageSelectionMode.Several) ||
                                    ((values.recipientId === undefined ||
                                      values.recipientId === 0 ||
                                      !(values.radius >= 0)) &&
                                      values.selectionMode === MessageSelectionMode.Neighbors) ||
                                    ((values.recipientId === undefined || values.recipientId === 0) &&
                                      values.selectionMode === MessageSelectionMode.One) ||
                                    ((values.groupId === undefined || values.groupId === 0) &&
                                      values.selectionMode === MessageSelectionMode.AffiliatesGroupOne) ||
                                    ((values.groupIds === undefined || values.groupIds.length === 0) &&
                                      values.selectionMode === MessageSelectionMode.AffiliatesGroupSeveral) ||
                                    ((values.dutyDate === undefined ||
                                      values.dutyDate === null ||
                                      (!values.dayOnly && !values.nightOnly && !values.dayNight)) &&
                                      values.selectionMode === MessageSelectionMode.OnDuty) ||
                                    values.title === undefined ||
                                    values.title === '' ||
                                    values.htmlContent === undefined ||
                                    values.htmlContent === '' ||
                                    values.selectionMode === undefined ||
                                    values.selectionMode === '' ||
                                    (values.selectionMode === MessageSelectionMode.Test && values.testEmailAddress == null)
                                  }
                                  text={<TranslatedMessage id={TLCode.GENERAL_Send} />}
                                />
                              </Tooltip>
                            </div>
                          </div>
                        </>
                      }
                    />
                  </Tabs>
                </form>
              );
            }}
          />
        </div>
      </Dialog>
    </>
  );
};

export interface UseMessageToSendProps {
  message: MessageToSend;
}

export const useMessageToSend = ({ message }: UseMessageToSendProps) => {
  const [messageToSend, setMessageToSend] = useState(message);

  useEffect(() => {
    setMessageToSend(message);
  }, [message]);

  return { messageToSend, setMessageToSend };
};

const DivColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 900px;
  margin-right: 20px;
`;

const boxed = css({
  border: '1px solid green',
  height: '100px',
});

const bckCol = css({
  backgroundColor: 'white',
});
const bckColGray = css({
  backgroundColor: 'lightGrey',
});
